import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, IconButton, Typography, Chip, Alert } from '@mui/material';
import { Send, StopCircleOutlined, SmartToyOutlined, PersonOutlined } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

import apiService from '../services/api';
import { useThemeMode } from '../contexts/ThemeContext';
import { AssistantMessage } from '../types';

const TOOL_LABELS: Record<string, string> = {
  list_apps: 'Looked up your apps',
  get_app_details: 'Fetched app details',
  find_app_by_key_suffix: 'Searched by key suffix',
  get_app_activity: 'Pulled recent activity',
  list_pending_requests: 'Checked pending requests',
  get_endpoint_catalog: 'Read the endpoint catalog',
};

const SUGGESTIONS = [
  'How do I rename an app?',
  'What does a 423 Locked error mean?',
  'Which endpoint should I use for gpt-4o-mini?',
  'How do I request a token limit increase?',
];

interface Props {
  fullHeight?: boolean;   // page mode fills viewport; bubble mode is fixed height
  compact?: boolean;      // slightly denser padding for the bubble
}

const AssistantChat: React.FC<Props> = ({ fullHeight = false, compact = false }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const textP = isDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.9)';
  const textM = isDark ? 'rgba(255,255,255,0.6)'  : 'rgba(0,0,0,0.6)';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const bg = isDark ? '#0f0f1e' : '#ffffff';
  const bubbleUser = isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe';
  const bubbleAssistant = isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc';

  const codeBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const textBlockSx = { color: textP, fontSize: '0.88rem', lineHeight: 1.55, m: 0, '&:not(:first-of-type)': { mt: 1 } };

  // Maps markdown elements to app-styled equivalents so assistant responses
  // (bold, lists, inline code) render properly instead of as literal
  // asterisks/backticks. User-typed messages stay plain text (see below) —
  // no reason to reinterpret what someone typed as markdown.
  const markdownComponents = {
    p: ({ children }: any) => <Typography component="p" sx={textBlockSx}>{children}</Typography>,
    strong: ({ children }: any) => <Box component="strong" sx={{ fontWeight: 700, color: textP }}>{children}</Box>,
    em: ({ children }: any) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
    ul: ({ children }: any) => <Box component="ul" sx={{ ...textBlockSx, pl: 2.5, mt: 0.5 }}>{children}</Box>,
    ol: ({ children }: any) => <Box component="ol" sx={{ ...textBlockSx, pl: 2.5, mt: 0.5 }}>{children}</Box>,
    li: ({ children }: any) => <Box component="li" sx={{ mb: 0.25 }}>{children}</Box>,
    a: ({ href, children }: any) => (
      <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{ color: '#3B82F6', textDecoration: 'underline' }}>
        {children}
      </Box>
    ),
    code: ({ className, children }: any) => {
      const isBlock = Boolean(className); // fenced ```code``` blocks carry a language- className; inline `code` doesn't
      return (
        <Box
          component={isBlock ? 'pre' : 'code'}
          sx={{
            fontFamily: '"JetBrains Mono","Courier New",monospace',
            fontSize: '0.8rem',
            bgcolor: codeBg,
            color: textP,
            borderRadius: '4px',
            px: isBlock ? 1.25 : 0.5,
            py: isBlock ? 1 : 0.15,
            display: isBlock ? 'block' : 'inline',
            overflowX: isBlock ? 'auto' : 'visible',
            whiteSpace: isBlock ? 'pre-wrap' : 'normal',
            wordBreak: 'break-word',
          }}
        >
          {children}
        </Box>
      );
    },
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => () => { controllerRef.current?.abort(); }, []);

  const send = (text: string) => {
    if (!text.trim() || streaming) return;

    const nextMessages: AssistantMessage[] = [
      ...messages,
      { role: 'user', content: text.trim() },
      { role: 'assistant', content: '', toolCalls: [] },
    ];
    setMessages(nextMessages);
    setInput('');
    setStreaming(true);

    // Backend expects only user/assistant turns (system prompt is server-side)
    const wire = nextMessages
      .slice(0, -1) // drop the empty in-flight assistant turn
      .map(m => ({ role: m.role, content: m.content }));

    controllerRef.current = apiService.streamAssistantChat(wire, {
      onDelta: (chunk) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role === 'assistant') {
            copy[copy.length - 1] = { ...last, content: last.content + chunk };
          }
          return copy;
        });
      },
      onToolCall: ({ name, id }) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role === 'assistant') {
            copy[copy.length - 1] = {
              ...last,
              toolCalls: [...(last.toolCalls || []), { id, name, status: 'running' }],
            };
          }
          return copy;
        });
      },
      onToolResult: ({ id }) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role === 'assistant') {
            copy[copy.length - 1] = {
              ...last,
              toolCalls: (last.toolCalls || []).map(tc => tc.id === id ? { ...tc, status: 'done' } : tc),
            };
          }
          return copy;
        });
      },
      onDone: () => setStreaming(false),
      onError: (message) => {
        setMessages(prev => {
          const copy = [...prev];
          const last = copy[copy.length - 1];
          if (last?.role === 'assistant') copy[copy.length - 1] = { ...last, error: message };
          return copy;
        });
        setStreaming(false);
      },
    });
  };

  const stop = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setStreaming(false);
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: fullHeight ? '100%' : 520,
      bgcolor: bg,
    }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: compact ? 1.5 : 3,
          py: compact ? 1.5 : 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ mt: 'auto', mb: 'auto', textAlign: 'center' }}>
            <SmartToyOutlined sx={{ fontSize: 40, color: textM, mb: 1 }} />
            <Typography sx={{ color: textP, fontSize: '0.95rem', fontWeight: 600, mb: 0.5 }}>
              Platform Assistant
            </Typography>
            <Typography sx={{ color: textM, fontSize: '0.8rem', mb: 2 }}>
              Ask about apps, permissions, requests, or common errors. Read-only — I'll never change anything for you.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', maxWidth: 380, mx: 'auto' }}>
              {SUGGESTIONS.map(s => (
                <Chip
                  key={s}
                  label={s}
                  onClick={() => send(s)}
                  sx={{
                    justifyContent: 'flex-start',
                    height: 'auto',
                    py: 0.75, px: 1,
                    fontSize: '0.8rem',
                    bgcolor: bubbleAssistant,
                    color: textP,
                    border: `1px solid ${border}`,
                    '&:hover': { bgcolor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {messages.map((m, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: m.role === 'user' ? bubbleUser : bubbleAssistant,
              color: m.role === 'user' ? '#3B82F6' : textM,
              flexShrink: 0,
            }}>
              {m.role === 'user' ? <PersonOutlined sx={{ fontSize: 16 }} /> : <SmartToyOutlined sx={{ fontSize: 16 }} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {m.toolCalls && m.toolCalls.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                  {m.toolCalls.map(tc => (
                    <Chip
                      key={tc.id}
                      size="small"
                      label={`${TOOL_LABELS[tc.name] || tc.name}${tc.status === 'running' ? '…' : ''}`}
                      sx={{
                        fontSize: '0.65rem', height: 20,
                        bgcolor: tc.status === 'running'
                          ? (isDark ? 'rgba(234,179,8,0.15)' : '#fef3c7')
                          : (isDark ? 'rgba(34,197,94,0.12)' : '#dcfce7'),
                        color: tc.status === 'running' ? '#EAB308' : '#22C55E',
                        border: 'none',
                      }}
                    />
                  ))}
                </Box>
              )}
              {m.role === 'assistant' ? (
                <Box sx={{ wordBreak: 'break-word' }}>
                  <ReactMarkdown components={markdownComponents}>
                    {m.content || (streaming && i === messages.length - 1 ? '…' : '')}
                  </ReactMarkdown>
                </Box>
              ) : (
                <Typography
                  component="div"
                  sx={{
                    color: textP,
                    fontSize: '0.88rem',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {m.content}
                </Typography>
              )}
              {m.error && (
                <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem', py: 0 }}>
                  {m.error}
                </Alert>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ borderTop: `1px solid ${border}`, p: compact ? 1 : 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Ask about apps, permissions, or errors…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          disabled={streaming}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
        />
        {streaming ? (
          <IconButton onClick={stop} sx={{ color: '#EF4444' }} title="Stop">
            <StopCircleOutlined />
          </IconButton>
        ) : (
          <IconButton onClick={() => send(input)} disabled={!input.trim()} sx={{ color: '#3B82F6' }} title="Send">
            <Send />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default AssistantChat;
